import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SectionHead, FadeUp } from "@/components/Reveal";
import { getBlog } from "@/lib/api";

const skeletons = [0, 1, 2];

export default function BlogPreview() {
  const [posts, setPosts] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    getBlog()
      .then((d) => setPosts(d.slice(0, 3)))
      .catch(() => setFailed(true));
  }, []);

  return (
    <section className="relative py-24 sm:py-32 border-t border-white/5" data-testid="blog-preview-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="Latest insights" title="From the Jiggly blog" />
          <FadeUp delay={0.15}>
            <Link
              to="/blog"
              data-testid="blog-view-all-link"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-neon hover:text-neonhover transition-colors"
            >
              View all articles
              <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="blog-preview-grid">
          {failed && (
            <p className="col-span-full text-mist" data-testid="blog-preview-error">
              Articles are resting. Refresh in a moment.
            </p>
          )}
          {!failed && !posts &&
            skeletons.map((s) => (
              <div key={s} className="skeleton rounded-2xl border border-white/5 h-72" data-testid={`blog-preview-skeleton-${s}`} />
            ))}
          {posts &&
            posts.map((post, i) => (
              <FadeUp key={post.slug} delay={i * 0.1} className="h-full">
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-preview-card-${i}`}
                  className="group flex flex-col h-full rounded-2xl border border-white/5 bg-surface p-7 transition-colors duration-300 hover:border-neon/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-neon border border-neon/25 bg-neon/10 rounded-full px-3 py-1.5">
                      {post.category}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-dim">{post.read_time}</span>
                  </div>
                  <h3 className="mt-6 font-display font-bold text-lg leading-snug text-white tracking-tight group-hover:text-neon transition-colors">
                    {post.title}
                  </h3>
                  <p className="mt-3 text-mist text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                  <div className="mt-auto pt-6 flex items-center justify-between">
                    <span className="font-mono text-[11px] text-dim">{post.date}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-neon">
                      Read
                      <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
        </div>
      </div>
    </section>
  );
}
